import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Link as MuiLink,
  Typography,
  Button,
  Alert,
  Paper,
  Avatar,
  Chip,
  Link,
  Stack,
  IconButton,
  Fab,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Link as RouterLink, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import PageContainer from "../components/PageContainer";
import EditCircleForm, {
  EDIT_CIRCLE_FORM_ID,
  type EditCircleFormFooterState,
} from "../components/Circles/EditCircleForm";
import { INITIAL_CIRCLE_FORM_FOOTER } from "../components/Circles/circleFormFooterState";
import { ManageCircleUsers } from "../components/Circles/ManageCircleUsers";
import CirclesSkeleton from "../components/Circles/CirclesSkeleton";
import { useCircles } from "../hooks/useCircles";
import { useNotices } from "../hooks/useNotices";
import { useCircleUsers } from "../hooks/useUserData";
import { computeCircleContentStats } from "../utils/circleContentStats";
import { driveHref, viewNoticesHref } from "../utils/circleNavigation";
import { useSnackbar } from "../context/SnackbarContext";
import {
  ActionModal,
  DeleteModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../components/shared";
import { useAuth } from "../context/AuthContext";
import { readCircleHandover } from "../domain/circles/circleHandover";
import { api } from "../services/api";
import {
  MANAGEMENT_MOBILE_FAB_SX,
  mobileFabWithScrollVisibility,
} from "../styles/managementShared";
import { useMobileFabVisibility } from "../hooks/useMobileFabVisibility";
import { lightTheme } from "../styles/theme";

/** Wide hero cover on desktop — profile + title overlay the image. */
const CIRCLE_HERO_ASPECT_RATIO = 21 / 6;
/** Shorter cover strip on mobile; details sit in the card below. */
const CIRCLE_MOBILE_COVER_ASPECT_RATIO = 2.35;

type CircleHeroStatLinkProps = {
  to: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onDark?: boolean;
};

function CircleHeroStatLink({
  to,
  icon,
  children,
  onDark = false,
}: CircleHeroStatLinkProps) {
  const theme = useTheme();

  return (
    <Link
      component={RouterLink}
      to={to}
      underline="hover"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        color: onDark
          ? alpha(theme.palette.common.white, 0.88)
          : "text.secondary",
        fontSize: "inherit",
        lineHeight: "inherit",
        "&:hover": {
          color: onDark ? "common.white" : "primary.main",
        },
      }}
    >
      {icon}
      {children}
    </Link>
  );
}

/** Old bookmarked URL → canonical circle hub. */
export function CircleMembersLegacyRedirect() {
  const { subcircleId } = useParams<{ subcircleId: string }>();
  if (!subcircleId) {
    return <Navigate to="/circles" replace />;
  }
  return <Navigate to={`/circles/${subcircleId}`} replace />;
}

/**
 * Full-page circle hub (desktop): edit details and manage members. Subcircles only.
 */
const CircleHomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactHero = useMediaQuery(theme.breakpoints.down("md"));
  const mobileFabVisible = useMobileFabVisibility(isMobile);
  const { subcircleId } = useParams<{ subcircleId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { showMessage } = useSnackbar();
  const { user, token } = useAuth();
  const { data: allCircles = [], isLoading, refetch } = useCircles();
  const { data: notices = [] } = useNotices();
  const { circleUsers, isLoading: circleUsersLoading, refetch: refetchCircleUsers } =
    useCircleUsers(subcircleId ?? "");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [editCircleFooter, setEditCircleFooter] =
    useState<EditCircleFormFooterState>(INITIAL_CIRCLE_FORM_FOOTER);
  const [startAdding, setStartAdding] = useState<string | undefined>();

  // Stable callback references for ManageCircleUsers (React.memo) — inline
  // arrows here would defeat that memo on every local state change (e.g.
  // typing in the delete-confirmation field), re-rendering the whole
  // 2,300-line component tree for no reason.
  const handleCloseManageCircleUsers = useCallback(() => {
    navigate("/circles");
  }, [navigate]);

  const handleCircleUsersChanged = useCallback(() => {
    void Promise.all([refetch(), refetchCircleUsers()]);
  }, [refetch, refetchCircleUsers]);

  const handleEditCircleFooterState = useCallback(
    (state: EditCircleFormFooterState) => {
      setEditCircleFooter(state);
    },
    []
  );

  useEffect(() => {
    if (editModalOpen) {
      setEditCircleFooter(INITIAL_CIRCLE_FORM_FOOTER);
    }
  }, [editModalOpen]);

  // Another page (Ask Noticeboard) can open a Circle with a User to add.
  // Read once, then cleared, so a reload or Back never opens it again.
  useEffect(() => {
    const { addUser } = readCircleHandover(location.state);
    if (!addUser) return;
    setStartAdding(addUser);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  const circle = useMemo(
    () => allCircles.find((c) => c.circleId === subcircleId),
    [allCircles, subcircleId]
  );

  const bannerUrl = circle?.orgBanner?.trim();
  const logoSrc =
    circle?.logo &&
    circle.logo.trim() !== "" &&
    !circle.logo.includes("placehold.co")
      ? circle.logo
      : "/placeholder.jpg";

  const createdLabel = circle
    ? new Date(circle.createdAt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";
  const displayedMemberCount = circleUsersLoading
    ? (circle?.memberCount ?? 0)
    : circleUsers.length;

  const { noticeCount, driveFileCount } = useMemo(() => {
    if (!subcircleId) {
      return { noticeCount: 0, driveFileCount: 0 };
    }
    return computeCircleContentStats(notices, subcircleId);
  }, [notices, subcircleId]);

  const handleEditProfilePhoto = () => {
    setEditModalOpen(true);
    showMessage({
      message: "Edit profile photo in Circle details.",
      severity: "info",
    });
  };

  const handleOpenDeleteModal = () => {
    setDeleteConfirmationText("");
    setDeleteModalOpen(true);
  };

  const handleConfirmDeleteCircle = async () => {
    if (!circle || !user?.schoolId || !token) return;
    if (deleteConfirmationText !== circle.name) return;

    setIsDeleting(true);
    try {
      await api.circles.removeSubCircle(circle.circleId, user.schoolId, token);
      showMessage({
        message: `"${circle.name}" was removed.`,
        severity: "success",
      });
      setDeleteModalOpen(false);
      navigate("/circles");
    } catch (error) {
      console.error("Error deleting circle:", error);
      showMessage({
        message:
          error instanceof Error
            ? error.message
            : "Failed to remove circle. Please try again.",
        severity: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <PageContainer sx={{ maxWidth: { xs: 1200, lg: 1480, xl: 1680 } }}>
        <CirclesSkeleton />
      </PageContainer>
    );
  }

  if (!subcircleId || !circle || !circle.isSubCircle) {
    return (
      <PageContainer sx={{ maxWidth: { xs: 1200, lg: 1480, xl: 1680 } }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          This circle was not found or cannot be managed here.
        </Alert>
        <Button variant="contained" onClick={() => navigate("/circles")}>
          Back to Circles
        </Button>
      </PageContainer>
    );
  }

  return (
    <PageContainer sx={{ maxWidth: { xs: 1200, lg: 1480, xl: 1680 } }}>
      {/*
        Match learning-center worksheets: inner width cap + mt on breadcrumbs
        (see WorksheetsHome / WorksheetsGrade / WorksheetBuilderEntry).
      */}
      <Box
        sx={{
          maxWidth: { xs: 1200, lg: "none" },
          mx: "auto",
          width: "100%",
        }}
      >
        <Box sx={{ mt: 1, mb: 2 }}>
          <Breadcrumbs
            separator={
              <ChevronRightIcon
                sx={{
                  fontSize: { xs: 16, sm: 17, md: 18 },
                  color: "text.secondary",
                  display: "block",
                }}
              />
            }
            aria-label="breadcrumb"
            sx={{
              maxWidth: "100%",
              "& .MuiBreadcrumbs-ol": {
                flexWrap: { xs: "nowrap", md: "wrap" },
                alignItems: "center",
                rowGap: 0.25,
              },
              "& .MuiBreadcrumbs-separator": {
                mx: { xs: 0.25, sm: 0.5, md: 1 },
              },
              "& .MuiBreadcrumbs-li:last-of-type": {
                minWidth: 0,
                overflow: "hidden",
              },
            }}
          >
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate("/")}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <HomeOutlinedIcon
                sx={{ fontSize: { xs: 20, sm: 21, md: 22 } }}
              />
            </MuiLink>
            <MuiLink
              component="button"
              underline="hover"
              color="text.secondary"
              onClick={() => navigate("/circles")}
              sx={{
                fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.875rem" },
                lineHeight: 1.25,
                whiteSpace: { xs: "nowrap", md: "normal" },
              }}
            >
              Circles
            </MuiLink>
            <Typography
              color="text.primary"
              noWrap
              title={circle.name}
              sx={{
                fontSize: { xs: "0.8125rem", sm: "0.875rem", md: "0.875rem" },
                lineHeight: 1.25,
              }}
            >
              {circle.name}
            </Typography>
          </Breadcrumbs>
        </Box>

      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          mb: 2,
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: {
              xs: CIRCLE_MOBILE_COVER_ASPECT_RATIO,
              md: CIRCLE_HERO_ASPECT_RATIO,
            },
            minHeight: { xs: 128, sm: 195, md: 210 },
            bgcolor: bannerUrl ? "grey.900" : "grey.800",
            backgroundImage: bannerUrl ? `url(${bannerUrl})` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            ...(bannerUrl
              ? {}
              : {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 45%, ${theme.palette.secondary.dark} 100%)`,
                }),
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              background: {
                xs: `linear-gradient(to bottom, transparent 55%, ${alpha(lightTheme.colors.pureBlack, 0.35)} 100%)`,
                md: `linear-gradient(
                  to top,
                  ${alpha(lightTheme.colors.pureBlack, 0.88)} 0%,
                  ${alpha(lightTheme.colors.pureBlack, 0.55)} 38%,
                  ${alpha(lightTheme.colors.pureBlack, 0.12)} 68%,
                  transparent 100%
                )`,
              },
            }}
          />

          {isCompactHero ? (
            <IconButton
              aria-label="Change cover"
              onClick={() => setEditModalOpen(true)}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 2,
                color: "common.white",
                bgcolor: alpha(lightTheme.colors.pureBlack, 0.45),
                backdropFilter: "blur(6px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                "&:hover": {
                  bgcolor: alpha(lightTheme.colors.pureBlack, 0.62),
                },
              }}
            >
              <PhotoCameraOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          ) : (
            <Button
              size="small"
              startIcon={<PhotoCameraOutlinedIcon sx={{ fontSize: 18 }} />}
              onClick={() => setEditModalOpen(true)}
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                zIndex: 2,
                color: "common.white",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8125rem",
                bgcolor: alpha(lightTheme.colors.pureBlack, 0.45),
                backdropFilter: "blur(6px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
                px: 1.5,
                py: 0.75,
                "&:hover": {
                  bgcolor: alpha(lightTheme.colors.pureBlack, 0.62),
                },
              }}
            >
              Change Cover
            </Button>
          )}

          {/* Desktop: title + actions on the cover */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              position: "absolute",
              inset: 0,
              zIndex: 1,
              flexDirection: "column",
              justifyContent: "flex-end",
              p: 3,
            }}
          >
            <Stack
              direction="row"
              spacing={2.5}
              alignItems="flex-end"
              justifyContent="space-between"
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-end"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    src={logoSrc}
                    alt=""
                    sx={{
                      width: 96,
                      height: 96,
                      border: "3px solid",
                      borderColor: alpha(theme.palette.common.white, 0.9),
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.35)",
                      bgcolor: "common.white",
                      fontSize: "1.75rem",
                      fontWeight: 700,
                    }}
                  >
                    {circle.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <IconButton
                    aria-label="Edit profile photo"
                    onClick={handleEditProfilePhoto}
                    sx={{
                      position: "absolute",
                      right: -4,
                      bottom: -4,
                      width: 30,
                      height: 30,
                      border: "2px solid",
                      borderColor: alpha(theme.palette.common.white, 0.9),
                      bgcolor: "grey.900",
                      color: "common.white",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.35)",
                      "&:hover": { bgcolor: "grey.800" },
                    }}
                  >
                    <PhotoCameraOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                <Box sx={{ minWidth: 0, pb: 0.25 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    sx={{ mb: 0.75 }}
                  >
                    <Typography
                      variant="h4"
                      component="h1"
                      noWrap
                      title={circle.name}
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.85rem",
                        lineHeight: 1.15,
                        color: "common.white",
                        textShadow: "0 1px 8px rgba(0,0,0,0.35)",
                      }}
                    >
                      {circle.name}
                    </Typography>
                    <Chip
                      size="small"
                      icon={
                        circle.isPublic ? (
                          <PublicOutlinedIcon sx={{ fontSize: "14px !important" }} />
                        ) : (
                          <LockOutlinedIcon sx={{ fontSize: "14px !important" }} />
                        )
                      }
                      label={circle.isPublic ? "Public" : "Private"}
                      sx={{
                        height: 26,
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        color: "common.white",
                        bgcolor: circle.isPublic
                          ? alpha(lightTheme.colors.violet500, 0.85)
                          : alpha(theme.palette.grey[700], 0.9),
                        border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
                        "& .MuiChip-icon": { color: "inherit" },
                      }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="center"
                    columnGap={1}
                    rowGap={0.25}
                    sx={{
                      color: alpha(theme.palette.common.white, 0.88),
                      fontSize: "0.875rem",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <PeopleOutlineIcon sx={{ fontSize: 17 }} />
                      <span>
                        {displayedMemberCount} member
                        {displayedMemberCount !== 1 ? "s" : ""}
                      </span>
                    </Stack>
                    <Typography
                      component="span"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.45),
                        userSelect: "none",
                      }}
                    >
                      •
                    </Typography>
                    <CircleHeroStatLink
                      to={viewNoticesHref(circle.circleId)}
                      onDark
                      icon={<CampaignOutlinedIcon sx={{ fontSize: 17 }} />}
                    >
                      {noticeCount} notice{noticeCount !== 1 ? "s" : ""}
                    </CircleHeroStatLink>
                    <Typography
                      component="span"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.45),
                        userSelect: "none",
                      }}
                    >
                      •
                    </Typography>
                    <CircleHeroStatLink
                      to={driveHref(circle.circleId)}
                      onDark
                      icon={<FolderOutlinedIcon sx={{ fontSize: 17 }} />}
                    >
                      {driveFileCount} drive file
                      {driveFileCount !== 1 ? "s" : ""}
                    </CircleHeroStatLink>
                    <Typography
                      component="span"
                      sx={{
                        color: alpha(theme.palette.common.white, 0.45),
                        userSelect: "none",
                      }}
                    >
                      •
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />
                      <span>Created {createdLabel}</span>
                    </Stack>
                  </Stack>
                  {circle.description ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.75,
                        color: alpha(theme.palette.common.white, 0.82),
                        lineHeight: 1.5,
                        maxWidth: 560,
                      }}
                    >
                      {circle.description}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <NoticeboardButton
                  variant="primary"
                  size="medium"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => setEditModalOpen(true)}
                  sx={{ boxShadow: "0 2px 12px rgba(0,0,0,0.2)" }}
                >
                  Edit Circle
                </NoticeboardButton>
                <NoticeboardButton
                  variant="secondary"
                  size="medium"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleOpenDeleteModal}
                  sx={{
                    color: "common.white",
                    borderColor: alpha(theme.palette.common.white, 0.55),
                    bgcolor: alpha(lightTheme.colors.pureBlack, 0.2),
                    backdropFilter: "blur(4px)",
                    "&:hover": {
                      borderColor: "common.white",
                      bgcolor: alpha(lightTheme.colors.pureBlack, 0.35),
                    },
                  }}
                >
                  Remove Circle
                </NoticeboardButton>
              </Stack>
            </Stack>
          </Box>
        </Box>

        {/* Mobile: profile + stats + actions below the cover */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            px: 2,
            pt: 0,
            pb: 2,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-end"
            sx={{ mt: -4.5, mb: 1.25 }}
          >
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <Avatar
                src={logoSrc}
                alt=""
                sx={{
                  width: 72,
                  height: 72,
                  border: "3px solid",
                  borderColor: "background.paper",
                  boxShadow: theme.shadows[3],
                  bgcolor: "common.white",
                  fontSize: "1.35rem",
                  fontWeight: 700,
                }}
              >
                {circle.name.charAt(0).toUpperCase()}
              </Avatar>
              <IconButton
                aria-label="Edit profile photo"
                onClick={handleEditProfilePhoto}
                size="small"
                sx={{
                  position: "absolute",
                  right: -2,
                  bottom: -2,
                  width: 28,
                  height: 28,
                  border: "2px solid",
                  borderColor: "background.paper",
                  bgcolor: "grey.900",
                  color: "common.white",
                  "&:hover": { bgcolor: "grey.800" },
                }}
              >
                <PhotoCameraOutlinedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>

            <Box sx={{ minWidth: 0, flex: 1, pb: 0.25 }}>
              <Stack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={0.75}
                sx={{ mb: 0.5 }}
              >
                <Typography
                  variant="h5"
                  component="h1"
                  noWrap
                  title={circle.name}
                  sx={{ fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}
                >
                  {circle.name}
                </Typography>
                <Chip
                  size="small"
                  icon={
                    circle.isPublic ? (
                      <PublicOutlinedIcon sx={{ fontSize: "14px !important" }} />
                    ) : (
                      <LockOutlinedIcon sx={{ fontSize: "14px !important" }} />
                    )
                  }
                  label={circle.isPublic ? "Public" : "Private"}
                  sx={{
                    height: 24,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    bgcolor: circle.isPublic
                      ? alpha(lightTheme.colors.violet500, 0.12)
                      : "action.selected",
                    color: circle.isPublic
                      ? lightTheme.colors.violet500
                      : "text.secondary",
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
              </Stack>
            </Box>
          </Stack>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: 1.5, mb: 1.5 }}
          >
            {displayedMemberCount} member{displayedMemberCount !== 1 ? "s" : ""}
            {" · "}
            <CircleHeroStatLink
              to={viewNoticesHref(circle.circleId)}
              icon={<CampaignOutlinedIcon sx={{ fontSize: 16 }} />}
            >
              {noticeCount} notice{noticeCount !== 1 ? "s" : ""}
            </CircleHeroStatLink>
            {" · "}
            <CircleHeroStatLink
              to={driveHref(circle.circleId)}
              icon={<FolderOutlinedIcon sx={{ fontSize: 16 }} />}
            >
              {driveFileCount} file{driveFileCount !== 1 ? "s" : ""}
            </CircleHeroStatLink>
            <Box component="span" sx={{ display: "block", mt: 0.25 }}>
              Created {createdLabel}
            </Box>
          </Typography>

          {circle.description ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5, mb: 1.5 }}
            >
              {circle.description}
            </Typography>
          ) : null}

          <Stack direction="row" spacing={1}>
            <NoticeboardButton
              variant="primary"
              size="small"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setEditModalOpen(true)}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Edit
            </NoticeboardButton>
            <NoticeboardButton
              variant="danger"
              size="small"
              startIcon={<DeleteOutlineIcon />}
              onClick={handleOpenDeleteModal}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Remove
            </NoticeboardButton>
          </Stack>
        </Box>
      </Paper>

      <ActionModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title="Circle details"
        subtitle="Update circle information and settings"
        maxWidth="lg"
        headerStyle="light"
        denseContent
        contentSx={{
          px: { xs: 2, sm: 2.5 },
          height: { md: 620 },
        }}
        footerSx={{ px: { xs: 2.5 } }}
        disableBackdropClick={editCircleFooter.isSubmitting}
        footer={
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1.5,
              width: "100%",
              justifyContent: "flex-end",
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <NoticeboardButton
              type="submit"
              form={EDIT_CIRCLE_FORM_ID}
              variant="primary"
              size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
              loading={editCircleFooter.isSubmitting}
              disabled={editCircleFooter.isSubmitting}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 200 },
                borderRadius: 1,
                "&.Mui-disabled": {
                  bgcolor: alpha(theme.palette.primary.main, 0.35),
                  color: alpha(theme.palette.common.white, 0.92),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              Update Circle
            </NoticeboardButton>
          </Box>
        }
      >
        <EditCircleForm
          key={circle.circleId}
          circle={circle}
          anchorSubmitToFooter
          onFooterStateChange={handleEditCircleFooterState}
          onCircleUpdated={() => {
            setEditModalOpen(false);
            void Promise.all([refetch(), refetchCircleUsers()]);
          }}
          onCancel={() => setEditModalOpen(false)}
        />
      </ActionModal>

      <DeleteModal
        open={deleteModalOpen}
        onClose={() => {
          if (isDeleting) return;
          setDeleteModalOpen(false);
          setDeleteConfirmationText("");
        }}
        title="Confirm Removal"
        subtitle="This action cannot be undone"
      >
        <Typography
          variant="body1"
          sx={{ mb: 3, fontSize: "1rem", lineHeight: 1.6 }}
        >
          Are you sure you want to remove the circle{" "}
          <Typography component="span" fontWeight={600}>
            &ldquo;{circle.name}&rdquo;
          </Typography>
          ? All members will be removed from this circle. This cannot be undone.
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            To confirm removal, type the circle name:{" "}
            <Typography component="span" fontWeight={600} color="error.main">
              {circle.name}
            </Typography>
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Type the circle name to confirm"
            value={deleteConfirmationText}
            onChange={(e) => setDeleteConfirmationText(e.target.value)}
            disabled={isDeleting}
            sx={{ mt: 1 }}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <NoticeboardButton
            variant="danger"
            onClick={handleConfirmDeleteCircle}
            loading={isDeleting}
            disabled={
              isDeleting || deleteConfirmationText !== circle.name
            }
          >
            Remove Circle
          </NoticeboardButton>
        </Box>
      </DeleteModal>

      <ManageCircleUsers
        layout="page"
        open
        onClose={handleCloseManageCircleUsers}
        subcircleId={circle.circleId}
        subcircleName={circle.name}
        onUsersChanged={handleCircleUsersChanged}
        startAdding={startAdding}
      />

      {isMobile && (
        <Fab
          color="primary"
          aria-label="Back to circles"
          onClick={() => navigate("/circles")}
          sx={mobileFabWithScrollVisibility(MANAGEMENT_MOBILE_FAB_SX, mobileFabVisible)}
        >
          <ArrowBackIcon sx={{ fontSize: 28 }} />
        </Fab>
      )}
      </Box>
    </PageContainer>
  );
};

export default CircleHomePage;
